import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/api/client";
import { PROJECTS_QUERY_KEY, projectRunsQueryKey } from "@/api/projects";
import { RUNS_QUERY_KEY, runQueryKey } from "@/api/runs";

// Wire-format mirrors the submission routes in
// src/flowstack/server/routes/submission.py (B2). Powers the GUI's
// CostPreviewPanel + SubmitTile flow + per-run Cancel/Retry buttons.

// Submit-time CPU/GPU resource-class override (flowstack#620). Mirrors
// the server's ``ResourceOverride`` wire model. Every field is optional;
// an omitted field keeps the tile's declared value. ``gpus`` is the
// routing-critical one — gpu>0 routes to the GPU cluster, gpu==0 to CPU
// (server _select_cluster_template). Omit the whole object to submit
// with the tile's declared resources (the safe default).
export interface ResourceOverride {
  cpu_cores?: number;
  ram_gb?: number;
  gpus?: number;
}

export interface CostPreviewRequest {
  tile_name: string;
  parameters?: Record<string, unknown>;
  // The user's current resource-class selection, so the live estimate
  // reflects the class they'll actually submit with (flowstack#620).
  resources?: ResourceOverride;
  /** Which dispatch path to quote (flowstack#2024). ``"flow_node"`` quotes the
   * resource row the parameters fire, as a flow compiles it; the default,
   * ``"tile_submit"``, quotes the baseline a standalone submit dispatches
   * (flowstack#2056). */
  context?: "tile_submit" | "flow_node";
}

export interface CostPreviewResponse {
  tile_name: string;
  estimated_cpu_hours: number;
  estimated_gpu_hours: number;
  estimated_total_usd: number;
  /** Always true today — the GUI renders an "estimated" badge.
   * Becomes false once the ledger surfaces per-run measurements. */
  estimated: boolean;
  /** Which pricing table produced the dollar figures (U0.3, C0.5).
   * "placeholder-estimate" = the Phase-0 placeholder table — the GUI
   * renders an "estimate" badge for exactly that value. Absent on
   * older servers → no badge. */
  pricing_basis?: string;
  /** The resolved shape the figures are priced from (flowstack#2024): the
   * tile's baseline after, for a ``"flow_node"`` quote, every override row the
   * parameters fire, then the submitter override. Absent on older servers. */
  resources?: ResolvedResources;
  /** True when no row could be evaluated for the parameters, so ``resources``
   * is the baseline rather than the step's row. */
  resource_row_unresolved?: boolean;
}

/** Mirrors the server's ``ResolvedResources``. Quantities are ``{val, unit}``. */
export interface ResolvedResources {
  cpu: number;
  gpu: number;
  memory: { val: number; unit: string };
  walltime_estimate: { val: number; unit: string };
}

export interface SubmitRunRequest {
  tile_name: string;
  parameters?: Record<string, unknown>;
  // Map of declared input-port name → artifact content_hash. The
  // backend resolves each artifact, asserts its typed object matches
  // the manifest's declared port type, and refuses the submission
  // when an upstream is `failed`-tagged unless
  // `override_failed_upstream` is true. M9 #60 slice 2.
  input_artifacts?: Record<string, string | string[]>;
  // When true, the submission proceeds even if one or more input
  // artifacts carry `scientific_status: "failed"`. The override is
  // logged + surfaced on `SubmitRunResponse.failed_input_artifacts_overridden`
  // for the audit trail.
  override_failed_upstream?: boolean;
  // Optional submit-time resource-class override (flowstack#620).
  // Applied to the resolved resource shape before cluster-template
  // selection. Omitted → the tile's declared resources (default-safe).
  resources?: ResourceOverride;
}

export interface SubmitRunResponse {
  run_id: string;
  tile_name: string;
  state: string;
  // Content hashes of any input artifacts whose
  // `scientific_status === "failed"` were accepted under the
  // override. Empty when no override was exercised (the typical
  // case). The GUI surfaces these so a reviewer can trace which
  // submissions consumed failed-tagged upstreams.
  failed_input_artifacts_overridden: string[];
}

/**
 * Error codes the submission route returns under HTTP 4xx/422.
 * Slice 3's artifact picker UI keys off these to render the right
 * banner / inline error per code. Pinned as a discriminated union
 * (per Rupert PR-297 r0) so the GUI's error-handling code can use a
 * `switch (code)` exhaustiveness check rather than ad-hoc string
 * matching against the wire `error_code`.
 */
export type SubmitRunErrorCode =
  // Manifest-side (no DB hit needed)
  | "tile_not_found"
  | "invalid_tile_name"
  | "manifest_schema_error"
  | "unknown_input_port"
  | "missing_required_input_port"
  | "many_cardinality_not_yet_supported"
  | "input_cardinality_mismatch"
  // Resource-class override (flowstack#620) — the override can't run
  // on the requested class (CUDA-only tile forced to CPU, or CPU-only
  // tile forced onto GPU).
  | "resource_override_incompatible"
  // Catalog-side (after the no-engine 503 guard)
  | "input_artifact_not_found"
  | "input_artifact_type_mismatch"
  | "failed_upstream_requires_override"
  // Engine / system
  | "database_not_configured";

export interface SubmitRunErrorDetail {
  error_code: SubmitRunErrorCode;
  message: string;
}

/** Wire shape for an HTTPException raised by the submission route. */
export interface SubmitRunErrorResponse {
  detail: SubmitRunErrorDetail;
}

/** One-shot cost preview. Doesn't persist anything. */
export async function fetchCostPreview(body: CostPreviewRequest): Promise<CostPreviewResponse> {
  return apiFetch<CostPreviewResponse>("/api/runs/cost-preview", {
    method: "POST",
    json: body,
  });
}

/**
 * The resource shape a tile resolves to for ``parameters``, read off the cost
 * preview so the Tile detail sheet and the quote come from one server-side
 * resolver (flowstack#2024). It asks for the ``"flow_node"`` quote: the sheet
 * shows a flow step, which compiles the row its parameters fire.
 * ``parameters === null`` means they are not known (the catalogue view), and
 * nothing is fetched. ``resources: null`` means the server predates the field.
 *
 * The key holds ``parameters`` itself, not a string of it: the query client
 * hashes keys with sorted object keys, so equal parameters share one entry. A
 * failed lookup is shown, not retried.
 */
export function useResolvedResources(tileName: string, parameters: Record<string, unknown> | null) {
  return useQuery({
    queryKey: ["resolved-resources", tileName, parameters],
    queryFn: () =>
      fetchCostPreview({ tile_name: tileName, parameters: parameters ?? {}, context: "flow_node" }),
    select: (response: CostPreviewResponse) => ({
      resources: response.resources ?? null,
      unresolved: response.resource_row_unresolved ?? false,
    }),
    enabled: Boolean(tileName) && parameters !== null,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/** Submit a new Tile run. Returns 201 with the new run_id. */
export function useSubmitRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: SubmitRunRequest): Promise<SubmitRunResponse> =>
      apiFetch<SubmitRunResponse>("/api/runs", {
        method: "POST",
        json: body,
      }),
    onSuccess: () => {
      // Invalidate the run / project caches so the new row appears
      // on the next poll without waiting for the 10s/30s stale.
      queryClient.invalidateQueries({ queryKey: RUNS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: PROJECTS_QUERY_KEY });
    },
  });
}

/** Cancel an in-flight run. Idempotent on already-terminal rows. */
export function useCancelRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (runId: string): Promise<{ run_id: string; state: string }> =>
      apiFetch<{ run_id: string; state: string }>(`/api/runs/${encodeURIComponent(runId)}/cancel`, {
        method: "POST",
      }),
    onSuccess: (_data, runId) => {
      queryClient.invalidateQueries({ queryKey: runQueryKey(runId) });
      queryClient.invalidateQueries({ queryKey: RUNS_QUERY_KEY });
    },
  });
}

/** Retry a failed run. Creates a new run with the same parameters.
 *
 * Per Rupert PR-259 r1: callers pass the run's actual ``project_slug``
 * so the project-scoped feed for the right project is invalidated.
 * The earlier hardcoded ``pilot-default`` worked only for the pilot
 * tenant's default project — once multiple projects exist the new
 * run would not appear on the right project page until 30s stale.
 * The variable is ``RetryRunArgs`` (not just ``runId``) so the
 * callsite stays explicit.
 */
export interface RetryRunArgs {
  runId: string;
  projectSlug?: string;
}

export function useRetryRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ runId }: RetryRunArgs): Promise<SubmitRunResponse> =>
      apiFetch<SubmitRunResponse>(`/api/runs/${encodeURIComponent(runId)}/retry`, {
        method: "POST",
      }),
    onSuccess: (_data, { projectSlug }) => {
      queryClient.invalidateQueries({ queryKey: RUNS_QUERY_KEY });
      // Bust the project-scoped run feed for THIS run's project so
      // the new run surfaces on the project detail page without
      // waiting for the 30s stale.
      if (projectSlug) {
        queryClient.invalidateQueries({ queryKey: projectRunsQueryKey(projectSlug) });
      }
      // Per Rupert PR-282 r1: the DAG canvas reads node state via the
      // overlay endpoint, whose query key prefix is
      // ``["flows", "dag-overlay", ...]``. Without busting it, the
      // failed badge on the just-retried node would stay stale until
      // the 5s overlay poll fires — long enough for a user to click
      // Retry again and spawn a sibling retry. Prefix-invalidate so
      // every flow's overlay refetches (cheap; there's typically just
      // one flow's overlay live at a time).
      queryClient.invalidateQueries({ queryKey: ["flows", "dag-overlay"] });
    },
  });
}
